import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { getPostById, getPulseById, getTestimonyById, listSaved } from '../utils/secureFunctions';
import TopNavigation from '../components/TopNavigation';
import MobileContainer from '../components/MobileContainer';
import Card from '../components/Card';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import MediaBox from '../components/MediaBox';
import { renderTextWithLinks } from '../utils/text';
import UserLink from '../components/UserLink';

type SavedType = 'post' | 'pulse' | 'testimony';
interface BaseItem { id: string; type: SavedType; userId: string; userName: string; userPhoto: string; }
interface PostItem extends BaseItem { type: 'post'; content: string; imageURL?: string; videoURL?: string; }
interface PulseItem extends BaseItem { type: 'pulse'; caption: string; videoURL: string; }
interface TestimonyItem extends BaseItem { type: 'testimony'; title: string; content: string; videoURL?: string; imageURL?: string; }
type SavedItem = PostItem | PulseItem | TestimonyItem;

const Saved: React.FC = () => {
  const [items, setItems] = useState<SavedItem[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!auth.currentUser) return;
      const combined: SavedItem[] = [];
      const savedRes = await listSaved({ limit: 200 });
      for (const item of savedRes.saved) {
        const id = item.contentId;
        if (item.contentType === 'post') {
          const pd = await getPostById(id).catch(() => null);
          if (pd?.post) {
            const dt = pd.post;
            combined.push({ id: dt.id, type: 'post', userId: dt.userId, userName: dt.userName, userPhoto: dt.userPhoto, content: dt.content || '', imageURL: dt.imageURL || undefined, videoURL: dt.videoURL || undefined });
          }
        } else if (item.contentType === 'pulse') {
          const rd = await getPulseById(id).catch(() => null);
          if (rd?.pulse) {
            const dt = rd.pulse;
            combined.push({ id: dt.id, type: 'pulse', userId: dt.userId, userName: dt.userName, userPhoto: dt.userPhoto, caption: dt.caption || '', videoURL: dt.videoURL });
          }
        } else if (item.contentType === 'testimony') {
          const td = await getTestimonyById(id).catch(() => null);
          if (td?.testimony) {
            const dt = td.testimony;
            combined.push({ id: dt.id, type: 'testimony', userId: dt.userId, userName: dt.userName, userPhoto: dt.userPhoto, title: dt.title || 'Testimony', content: dt.content || '', videoURL: dt.videoURL || undefined, imageURL: dt.imageURL || undefined });
          }
        }
      }
      setItems(combined);
    };
    load();
  }, []);

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={{ background: '#F5EDE1', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={{ paddingTop: 0, marginLeft: isMobile ? 0 : '300px', marginRight: isMobile ? 0 : '340px' }}>
      <MobileContainer maxWidth={800}>
        <h2 style={{ marginTop: 16 }}>Saved</h2>
        {items.length === 0 ? (
          <p style={{ color: '#5B6E6E' }}>Nothing saved yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map((it) => (
              <Card key={`${it.type}-${it.id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <ImageWithFallback src={it.userPhoto} alt="" style={{ width: 40, height: 40, borderRadius: '50%' }} />
                  <UserLink userId={it.userId} as="strong">{it.userName}</UserLink>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#5B6E6E' }}>{it.type === 'pulse' ? 'VIDEO' : it.type.toUpperCase()}</span>
                </div>
                {it.type === 'post' && (
                  <>
                    <div style={{ marginBottom: 8 }}>{renderTextWithLinks((it as PostItem).content)}</div>
                    {(it as PostItem).imageURL && <MediaBox src={(it as PostItem).imageURL!} type="image" />}
                    {(it as PostItem).videoURL && <MediaBox src={(it as PostItem).videoURL!} type="video" controls contain />}
                  </>
                )}
                {it.type === 'pulse' && (
                  <>
                    <div style={{ marginBottom: 8 }}>{(it as PulseItem).caption}</div>
                    <MediaBox src={(it as PulseItem).videoURL} type="video" controls contain />
                  </>
                )}
                {it.type === 'testimony' && (
                  <>
                    <div style={{ marginBottom: 4, fontWeight: 600 }}>{(it as TestimonyItem).title}</div>
                    <div style={{ marginBottom: 8 }}>{renderTextWithLinks((it as TestimonyItem).content)}</div>
                    {(it as TestimonyItem).imageURL && <MediaBox src={(it as TestimonyItem).imageURL!} type="image" />}
                    {(it as TestimonyItem).videoURL && <MediaBox src={(it as TestimonyItem).videoURL!} type="video" controls contain />}
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </MobileContainer>
      </div>
    </div>
  );
};

export default Saved;
